<script setup lang="ts">
import { ref, computed, reactive } from 'vue';
import cloneDeep from 'lodash/cloneDeep';
import { useI18n } from 'vue-i18n';
import { getCurrentLocale } from '@/i18n';
import { TOTAL_COST, HOTEL_COST_ADJ } from '@/constants';
import { getTextVariant } from '@/utils/stringUtils';
import { formatDate } from '@/utils/dateUtils';
import { useWorksheetComposable } from './useWorksheetComposable';
import { useConvertTextComposable } from '@/components/modal/useConvertTextComposable';
import { useDialog } from 'primevue/usedialog';
import Button from 'primevue/button';
import DataTable, { DataTableCellEditCancelEvent } from 'primevue/datatable';
import InputNumber from 'primevue/inputnumber';
import InputText from 'primevue/inputtext';
import TableColumn from 'primevue/column';
import RadioButton from 'primevue/radiobutton';
import PriceBreakdownModal from '@/components/modal/PriceBreakdownModal.vue';

interface Props {
  pricing?: any;
  depDate?: any;
  additionalTax?: any;
  classOutbound?: any;
  classInbound?: any;
  isView?: boolean;
  tour?: any;
  tourWorkSheet?: any;
}
const props = defineProps<Props>();

const emits = defineEmits<{
  getFareSearch: [value: any];
  'update:pricing': [value: any];
  pricing: [value: any];
  updateNetMargin: [value: any];
}>();

const pricingData = computed({
  get() {
    return formatDataPricing(props.pricing);
  },
  set(value) {
    emits('update:pricing', value);
  }
});

const { filterPriceList, PRICE_KEY, authorizations, titleHotel, formatDataBeforeSaveRow, checkDateQuote, getBaseFare, getArdTaxes, getNetMargin, checkEditChildInput, handleChildInput } = useWorksheetComposable();
const { handleReplaceValueToName } = useConvertTextComposable();

const { t } = useI18n({ useScope: 'global' });
const dialog = useDialog();
const lang = getCurrentLocale();

const editingRowsHotel = ref([]);
const oldChildValue = ref([]);
const flightVendor = ref('VAC');
const flightVendorList = ['VAC', 'GROUPSB', 'PUBLISHED'];
const isShowError = ref(false);
const validationInputs = reactive<Record<string, boolean>>({});

const formatDataPricing = (pricing: any) => {
  let price = props.isView ? filterPriceList(pricing, authorizations?.worksheet_tour_hotel_header) : filterPriceList(pricing, authorizations?.worksheet_tour_hotel_header, authorizations?.worksheet_tour_additional);
  flightVendor.value = props?.additionalTax?.vendor && flightVendorList.includes(props?.additionalTax?.vendor) ? props?.additionalTax?.vendor : 'VAC';
  price = getBaseFare(props.additionalTax?.priceBreak ?? [], price);
  price = getArdTaxes(props.additionalTax?.priceBreak ?? [], price, props.depDate, props.additionalTax?.ardTax, props.tourWorkSheet?.created);
  price = getNetMargin(props.additionalTax?.priceBreak ?? [], price, props.depDate, props.additionalTax?.ardTax, props.tourWorkSheet?.created);
  emits('pricing', price);
  return price;
};

const convertOccNameTable = (rowName: string) => {
  return titleHotel.find((th) => th.key === rowName)?.name.replace(t('worksheet.hotel_ebb'), t('worksheet.tour_ebb'));
};

const getChildPrices = (data: any) => {
  oldChildValue.value = cloneDeep(data.childPrices);
};

const getMarAdjAndTotalMar = (changeData: any, key: string, clonePrice: any) => {
  const tempMarAdj = Array(changeData[key].length);
  const tempTotalMar = Array(changeData[key].length);
  for (let i = 0; i < changeData[key].length; i++) {
    tempMarAdj[i] = ((Number(clonePrice.marAdj[key][i]) || 0) + changeData[key][i]).toFixed(2);
    tempTotalMar[i] = ((Number(clonePrice.totalMar[key][i]) || 0) + changeData[key][i]).toFixed(2);
  }
  return { tempMarAdj: tempMarAdj, tempTotalMar: tempTotalMar };
};

const getSellingPriceAndTotalMar = (changeData: any, key: string, clonePrice: any) => {
  const tempSellingPrice = Array(changeData[key].length);
  const tempTotalMar = Array(changeData[key].length);
  for (let i = 0; i < changeData[key].length; i++) {
    tempSellingPrice[i] = ((Number(clonePrice.sellingPrice[key][i]) || 0) + changeData[key][i]).toFixed(2);
    tempTotalMar[i] = ((Number(clonePrice.totalMar[key][i]) || 0) + changeData[key][i]).toFixed(2);
  }
  return { tempSellingPrice: tempSellingPrice, tempTotalMar: tempTotalMar };
};

const getSellingPrice = (changeData: any, key: string, clonePrice: any) => {
  const temp = Array(changeData[key].length);
  for (let i = 0; i < changeData[key].length; i++) {
    temp[i] = ((Number(clonePrice.sellingPrice[key][i]) || 0) + changeData[key][i]).toFixed(2);
  }
  return temp;
};

const handleEditSellingPrice = (newPrice: any, changeData: any, clonePrice: any) => {
  for (const key in changeData) {
    if (key === 'childPrices') {
      const temp = getMarAdjAndTotalMar(changeData, key, clonePrice);
      newPrice.marAdj[key] = temp.tempMarAdj;
      newPrice.totalMar[key] = temp.tempTotalMar;
    } else {
      newPrice.marAdj[key] = ((Number(clonePrice.marAdj[key]) || 0) + changeData[key]).toFixed(2);
      newPrice.totalMar[key] = ((Number(clonePrice.totalMar[key]) || 0) + changeData[key]).toFixed(2);
    }
  }
};

const handleEditMarginAdjustment = (newPrice: any, changeData: any, clonePrice: any) => {
  for (const key in changeData) {
    if (key === 'childPrices') {
      const temp = getSellingPriceAndTotalMar(changeData, key, clonePrice);
      newPrice.sellingPrice[key] = temp.tempSellingPrice;
      newPrice.totalMar[key] = temp.tempTotalMar;
    } else {
      newPrice.sellingPrice[key] = ((Number(clonePrice.sellingPrice[key]) || 0) + changeData[key]).toFixed(2);
      newPrice.totalMar[key] = ((Number(clonePrice.totalMar[key]) || 0) + changeData[key]).toFixed(2);
    }
  }
};

const handleEditCostAdjustment = (newPrice: any, changeData: any, clonePrice: any, newData: any) => {
  for (const key in changeData) {
    newPrice[HOTEL_COST_ADJ] = newData;
    if (key === 'childPrices') {
      const temp = getSellingPrice(changeData, key, clonePrice);
      newPrice.sellingPrice[key] = temp;
    } else {
      newPrice.sellingPrice[key] = ((Number(clonePrice.sellingPrice[key]) || 0) + changeData[key]).toFixed(2);
    }
  }
};

const handleChildTourInput = (value: any, field: string, indexColumn: number) => {
  isShowError.value = !handleChildInput(value, field, indexColumn, validationInputs);
};

const handleCancleChildInput = (editorCancelCallback: any) => {
  editorCancelCallback();
  isShowError.value = false;
};

const handleSaveChildInput = (editorSaveCallback: any, data: any) => {
  if (data.rowName !== 'occupanciesEdit') {
    editorSaveCallback();
  } else {
    checkEditChildInput(editorSaveCallback, isShowError.value);
  }
};

const onRowEditSave = async ({ newData, data }: any) => {
  const { rowCanAutoCalulations: row, editedRow, changeData }: any = formatDataBeforeSaveRow(newData, data, oldChildValue.value);
  const clonePrice = cloneDeep(props.pricing);
  const newPrice = cloneDeep(props.pricing);
  switch (editedRow) {
    case row.sellingPrice:
      handleEditSellingPrice(newPrice, changeData, clonePrice);
      break;
    case row.marginAdjustment:
      handleEditMarginAdjustment(newPrice, changeData, clonePrice);
      break;
    case row.costAdjustment:
      handleEditCostAdjustment(newPrice, changeData, clonePrice, newData);
      break;
  }
  newPrice[newData.rowName] = formatChildPrices(newData);
  await emits('update:pricing', newPrice);
  emits('updateNetMargin', pricingData.value);
};

const formatChildPrices = (tourData: any) => {
  if (tourData.childPrices && Array.isArray(tourData.childPrices)) {
    tourData.childPrices = tourData.childPrices.map((price) => {
      if (typeof price === 'number') {
        return price.toFixed(2);
      }
      return price;
    });
  }
  return tourData;
};

const onRowEditCancel = (event: DataTableCellEditCancelEvent, listValue: any) => {
  listValue[event.index].childPrices = oldChildValue.value;
};

const openPriceBreakModal = () => {
  const dialogRef = dialog.open(PriceBreakdownModal, {
    props: {
      header: t('worksheet.price_breakdown'),
      modal: true
    },
    data: {
      getPostSearch: props.additionalTax?.priceBreak ?? []
    },
    emits: {
      onClose: () => {
        dialogRef.close();
      }
    }
  });
};

const renderTourTypeMessage = (tour: any) => {
  const tourTypeText = tour.tourType ? ` - ${handleReplaceValueToName(tour.tourType, 'tourType') ?? ''}` : '';
  const guidedText = tour.guided ? ` - ${handleReplaceValueToName(tour.guided?.toUpperCase(), 'guided') ?? ''}` : '';
  return `${tourTypeText}${guidedText}`;
};

const renderHeaderTable = (tourData: any) => {
  return `(${formatDate(tourData.depDate, 'dd-LLL-yyyy', false, lang)}) ${getTextVariant(lang, tourData?.tourNameEn, tourData?.tourNameFr)} - ${getTextVariant(lang, tourData?.tourInclusionEn, tourData?.tourInclusionFr)} (${getTextVariant(lang, tourData?.tourDurationEn, tourData?.tourDurationFr)})${renderTourTypeMessage(tourData)} (${tourData?.ttsId.slice(6)})`;
};

const getFareSearch = () => {
  emits('getFareSearch', { flightVendor: flightVendor.value, pricing: pricingData.value });
};
</script>
<template>
  <div>
    <div class="pricing">
      <div class="lead-pricing flex align-items-center justify-content-between" v-if="!isView">
        {{ renderHeaderTable(tour) }}
      </div>
      <div class="pricing-detail">
        <DataTable
          rowGroupMode="subheader"
          v-model:editingRows="editingRowsHotel"
          :value="pricingData"
          class="w-full"
          editMode="row"
          data-key="rowName"
          showGridlines
          @row-edit-save="onRowEditSave($event)"
          @row-edit-cancel="onRowEditCancel($event, pricingData)"
        >
          <TableColumn header="" dataType="string" class="relative">
            <template #body="{ data }">
              <div :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'text-900 font-bold', { 'total-row': data.key === TOTAL_COST && isView }]">
                <span
                  >{{ convertOccNameTable(data.rowName) }}<i v-if="data.rowName === 'ardTaxes'" class="pi pi-plus-circle ml-1 cursor-pointer" :class="{ disabled: !checkDateQuote(depDate, tourWorkSheet?.created) }" @click="openPriceBreakModal()"></i
                ></span>
              </div>
            </template>
          </TableColumn>
          <TableColumn field="singlePrice" :header="$t('book_form.single')" dataType="number" class="relative">
            <template #body="{ data, field, index }">
              <div
                v-if="index"
                :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header', { 'total-row': data.key === TOTAL_COST && isView }]"
              >
                <span :class="data.key === PRICE_KEY.HEADER && 'opacity-0'">{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                <div v-if="authorizations?.worksheet_tour_additional?.process_button && data.rowName == 'additionInformation'" class="flex justify-content-end pr-2 flight-vendor">
                  <span class="uppercase">{{ $t('worksheet.outbound') }}</span>
                </div>
              </div>
            </template>
            <template #editor="{ data, field, index }">
              <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" class="w-full single-price-edit" />
            </template>
          </TableColumn>
          <TableColumn field="doublePrice" :header="$t('book_form.double')" dataType="number" class="relative">
            <template #body="{ data, field, index }">
              <div
                v-if="index"
                :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header', { 'total-row': data.key === TOTAL_COST && isView }]"
              >
                <span :class="data.key === PRICE_KEY.HEADER && 'hidden'">{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                <div v-if="authorizations?.worksheet_tour_additional?.process_button && data.rowName == 'additionInformation'" class="h-full align-items-center flex justify-content-end pr-2">
                  <InputText :modelValue="classOutbound" class="w-10 pl-3" disabled />
                </div>
              </div>
            </template>
            <template #editor="{ data, field, index }">
              <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" class="w-full double-price-edit" />
            </template>
          </TableColumn>
          <TableColumn field="triplePrice" :header="$t('book_form.triple')" dataType="number" class="relative">
            <template #body="{ data, field, index }">
              <div
                v-if="index"
                :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header', { 'total-row': data.key === TOTAL_COST && isView }]"
              >
                <span :class="data.key === PRICE_KEY.HEADER && 'opacity-0'">{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                <div v-if="authorizations?.worksheet_tour_additional?.process_button && data.rowName == 'additionInformation'" class="flex justify-content-end pr-2 flight-vendor">
                  <span class="uppercase">{{ $t('worksheet.return') }}</span>
                </div>
              </div>
            </template>
            <template #editor="{ data, field, index }">
              <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" class="w-full triple-price-edit" />
            </template>
          </TableColumn>
          <TableColumn field="quadPrice" :header="$t('book_form.quad')" dataType="number" class="relative w-7rem">
            <template #body="{ data, field, index }">
              <div
                v-if="index"
                :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header', { 'total-row': data.key === TOTAL_COST && isView }]"
              >
                <span :class="data.key === PRICE_KEY.HEADER && 'hidden'">{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                <div v-if="authorizations?.worksheet_tour_additional?.process_button && data.rowName == 'additionInformation'" class="h-full align-items-center flex justify-content-end pr-2">
                  <InputText :modelValue="classInbound" class="w-10 pl-3" disabled />
                </div>
              </div>
            </template>
            <template #editor="{ data, field, index }">
              <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" class="w-full quad-price-edit" />
            </template>
          </TableColumn>
          <TableColumn field="fiveInRoomPrice" :header="$t('book_form.5_in_room')" dataType="number" class="relative">
            <template #body="{ data, field, index }">
              <div
                v-if="index"
                :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header', { 'total-row': data.key === TOTAL_COST && isView }]"
              >
                <span :class="data.key === PRICE_KEY.HEADER && 'opacity-0'">{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                <div v-if="authorizations?.worksheet_tour_additional?.process_button && data.rowName == 'additionInformation'" class="flex justify-content-end pr-3 flight-vendor flight-vendor-lg">
                  <span class="mt-1">VAC</span>
                  <div class="pl-2"><RadioButton v-model="flightVendor" name="flightVendor" value="VAC" /></div>
                </div>
              </div>
            </template>
            <template #editor="{ data, field, index }">
              <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" class="w-full five-in-room-price-edit" />
            </template>
          </TableColumn>
          <TableColumn field="sixInRoomPrice" :header="$t('book_form.6_in_room')" dataType="number" class="relative w-7rem">
            <template #body="{ data, field, index }">
              <div
                v-if="index"
                :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header', { 'total-row': data.key === TOTAL_COST && isView }]"
              >
                <span :class="data.key === PRICE_KEY.HEADER && 'opacity-0'">{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                <div v-if="authorizations?.worksheet_tour_additional?.process_button && data.rowName == 'additionInformation'" class="flex justify-content-end px-2 flight-vendor flight-vendor-lg">
                  <span class="uppercase mt-1">{{ $t('worksheet.groupsb') }}</span>
                  <div class="pl-2"><RadioButton v-model="flightVendor" name="flightVendor" value="GROUPSB" /></div>
                </div>
              </div>
            </template>
            <template #editor="{ data, field, index }">
              <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" class="w-full six-in-room-price-edit" />
            </template>
          </TableColumn>
          <TableColumn field="childPrices" :header="$t('common.child')" class="min-w-8rem relative parent-column" v-for="(column, indexColumn) in pricingData[0].childPrices" :key="'childColumn' + indexColumn">
            <template #body="{ data, field, index }">
              <div
                v-if="index === 0 || true"
                :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header ', { 'total-row': data.key === TOTAL_COST && isView }]"
              >
                <span :class="data.key === PRICE_KEY.HEADER && 'opacity-0'">
                  {{ data.rowName && data.rowName === 'occupanciesEdit' ? data[field]?.[indexColumn] : Number(data[field]?.[indexColumn]) ? Number(data[field]?.[indexColumn]).toFixed(2) : '0.00' }}
                </span>
                <div v-if="authorizations?.worksheet_tour_additional?.process_button && data.rowName == 'additionInformation'" class="flex justify-content-end px-2 flight-vendor flight-vendor-lg">
                  <span class="uppercase mt-1" v-if="indexColumn === 0">{{ $t('worksheet.published') }}</span>
                  <div class="pl-2" v-if="indexColumn === 0"><RadioButton v-model="flightVendor" value="PUBLISHED" name="flightVendor" /></div>
                </div>
              </div>
              <div
                v-else
                :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header ', { 'total-row': data.key === TOTAL_COST && isView }]"
              >
                <span :class="data.key === PRICE_KEY.HEADER && 'opacity-0'">{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
              </div>
            </template>
            <template #editor="{ data, field, index }">
              <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field][indexColumn]" class="w-full child-price-edit" />
              <InputText v-else v-model="data[field][indexColumn]" @input="handleChildTourInput(data[field][indexColumn], field, indexColumn)" class="w-full child-header-edit" />
            </template>
          </TableColumn>
          <TableColumn field="infantPrice" :header="$t('common.infant')" dataType="number" class="relative">
            <template #body="{ data, field, index }">
              <div v-if="index === 0">
                {{ data[field] }}
              </div>
              <div
                v-else
                :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header', { 'total-row': data.key === TOTAL_COST && isView }]"
              >
                <span :class="data.key === PRICE_KEY.HEADER && 'opacity-0'">{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
              </div>
              <div v-if="data.key === TOTAL_COST || data.key === PRICE_KEY.HEADER" class="margin-box"></div>
            </template>
            <template #editor="{ data, field, index }">
              <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" class="w-full infant-price-edit" />
              <InputText v-else v-model="data[field]" class="w-full infant-header-edit" />
            </template>
          </TableColumn>
          <TableColumn v-if="!isView" :rowEditor="true" class="text-center relative w-5rem" :header="$t('common.action')">
            <template #body="{ editorInitCallback, data }">
              <div :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header right-0']">
                <span v-if="data.key !== TOTAL_COST && data.key !== PRICE_KEY.HEADER && data.canEdit" @click="getChildPrices(data)"><i class="pi pi-pencil" @click="editorInitCallback"></i></span>
              </div>
              <p v-if="data.key === TOTAL_COST || data.key === PRICE_KEY.HEADER" class="opacity-0" :class="{ hidden: data.rowName === 'additionInformation' }">{{ $t('common.edit') }}</p>
              <div v-if="authorizations?.worksheet_tour_additional?.process_button && data.rowName == 'additionInformation'">
                <Button id="search-fare-btn" class="action-button" size="small" severity="danger" :label="$t('common.buttons.process')" @click="getFareSearch()" />
              </div>
            </template>
            <template #editor="{ data, editorSaveCallback, editorCancelCallback }">
              <div class="flex">
                <i class="pi pi-check edit-pi" @click="handleSaveChildInput(editorSaveCallback, data)"></i>
                <i class="pi pi-times edit-pi" @click="handleCancleChildInput(editorCancelCallback)"></i>
              </div>
            </template>
          </TableColumn>
        </DataTable>
      </div>
    </div>
  </div>
</template>
<style scoped lang="scss">
:deep() {
  .p-inputtextarea:disabled {
    opacity: 1;
    cursor: not-allowed;
  }

  .price-header {
    position: absolute;
    top: 0px;
    right: -3px;
    left: -3px;
    bottom: 0;
  }

  .background-opacity {
    background: rgba($color: #ddd, $alpha: 0.5);
  }
}

:deep(.p-inputnumber .p-inputnumber-input) {
  width: 100%;
}
</style>
